import { useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { bookingTypes } from '../../config/config';
import {
	getFlightSearchUrl,
	getCJSLocationSearchUrl,
	getCJSSearchUrl
} from '../../lib/utils/api.service';
import { CJS_BRAND } from '../../lib/utils';
import {
	CABIN_CLASSES,
	DATE_FORMATS,
	TRIP_TYPES,
	PAYMENT_TYPES,
	DATEPICKER_ERROR_MESSAGE,
	TYPEAHEAD_MESSAGES
} from '../../lib/utils/constants';
import { Passengers } from '../../lib/utils/composition.service';
import handleInputFocus from '../../lib/utils/handle-input-focus';
import { refsToUrlParams } from '../../lib/utils/refs';
import { multicityEnabled } from '../../lib/utils/feature-toggle';
import { RoomComposition, Rooms } from '../../lib/utils/composition.service';

import { useApplication } from '../../hooks/useApplication';

import { BookingType } from '../../types/config';
import { TripTypesType } from '../../types/flights';
import { CJSSearchRequest, FlightSearchRequest } from '../../types/api';
import {
	FLIGHT_ONLY,
	FLIGHT_HOTEL,
	HOLIDAY,
	HOTEL,
	PRODUCT_TYPES,
	REWARD_FLIGHT
} from '../../types/criteria';

import PassengerFieldComponent from '../passenger/passenger-field.component';
import SeasonalMessageComponent from '../seasonal-message/seasonal-message.component';
import TypeaheadComponent from '../typeahead/typeahead.component';
import DiscountCodeComponent from '../discount-code-field/discount-code.component';
import DatePickerFieldComponent from '../date-picker-field/date-picker-field.component';
import DynamicListSelectorComponent from '../dynamic-list-selector/dynamic-list-selector.component';
import MultiCityFormComponent from '../multicity-form/multicity-form.component';

import {
	SwitchComponent,
	SwitchLabelPosition,
	SwitchSize
} from '@vaa-component-lib/component.atom.switch';
import {
	IconActionSearchComponent,
	IconTravelTakeOffComponent,
	IconTravelGlobeMarkerComponent,
	IconFeedbackInfoComponent
} from '@vaa-component-lib/component.atom.icons';
import {
	AlertComponent,
	AlertEmphasis,
	AlertStatus
} from '@vaa-component-lib/component.molecule.alert';
import {
	ButtonComponent,
	ButtonPriority,
	ButtonSize,
	ButtonType,
	IconPosition
} from '@vaa-component-lib/component.atom.button';

import {
	ServerError,
	FormGrid,
	FormOptions,
	Div,
	column,
	option,
	discount,
	destination,
	origin,
	readOnly,
	date,
	passengers,
	submit,
	rewardText
} from './forms.styles';

import {
	tripType,
	cabin,
	mainColumn,
	inputWidths,
	multiCityButton,
	multiCityDiscount,
	multiCityInput,
	multiCtaButton,
	buttonWidths,
	flyingColumn,
	roomsModal,
	modalContainer,
	roomsModalText,
	roomsModalHeader
} from './flight.styles';

import {
	TypographyComponent,
	TypographyVariant,
	TypographySize,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import { ModalComponent } from '@vaa-component-lib/component.molecule.modal';
import RoomCompositionComponent from '../room/room-composition.component';

interface FlightOnlyComponentProps {
	bookingType: BookingType;
}

const FlightOnlyComponent = ({ bookingType }: FlightOnlyComponentProps) => {
	const { application, setApplication } = useApplication();
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [holidaySubmitting, setHolidaySubmitting] = useState<boolean>(false);
	const [showHolidayBtn, setShowHolidayBtn] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [defaultRoomValues, setDefaultRoomValues] = useState([
		{
			adults: 2,
			childAges: [],
			infants: 0
		}
	]);
	const [bookingTypeState, setBookingTypeState] =
		useState<BookingType>(bookingType);

	const ref = useRef<HTMLDivElement>(null);
	const criteria =
		bookingType.type === application?.criteria?.searchType
			? application?.criteria
			: null;

	const [selectedTripType, setSelectedTripType] = useState<TripTypesType>(
		() => {
			const typedCriteria = criteria as FLIGHT_ONLY | REWARD_FLIGHT;

			if (
				typedCriteria &&
				(typedCriteria.origin?.length > 2 ||
					typedCriteria.destination?.length > 2 ||
					typedCriteria.departing?.length > 2)
			) {
				return TRIP_TYPES.MULTICITY.value;
			}

			return typedCriteria?.tripType || TRIP_TYPES.ROUND_TRIP.value;
		}
	);

	const defaults = {
		tripType: selectedTripType,
		cabinClass:
			(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.cabin ||
			CABIN_CLASSES.ALL_CABINS.code,
		paymentType:
			(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.paymentType ||
			PAYMENT_TYPES.MONEY.value
	};

	const formMethods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onChange'
	});

	const {
		register,
		control,
		watch,
		handleSubmit,
		formState: { errors },
		setError,
		setValue
	} = formMethods;

	const tripTypeWatcher = watch('trip_type');
	const tripOriginCodeWatcher = watch('origin.airportCode');
	const tripOriginWatcher = watch('origin.cityName')?.toLowerCase() || '';
	const tripDestinationWatcher =
		watch('destination.cityName')?.toLowerCase().replace(/\s+/g, '-') || '';
	const tripDateWatcher = watch('departing');
	const tripRoomsWatcher = watch('passengerBreakdown');

	useEffect(() => {
		const shouldFetchHolidayUrl =
			tripOriginWatcher &&
			tripDestinationWatcher &&
			bookingTypeState.type !== PRODUCT_TYPES.REWARD_FLIGHT &&
			tripTypeWatcher === TRIP_TYPES.ROUND_TRIP.value;

		if (!shouldFetchHolidayUrl) {
			setShowHolidayBtn(false);
			return;
		}

		const fetchHolidayUrl = async () => {
			try {
				const response = await getCJSLocationSearchUrl({
					brand: CJS_BRAND.VAH,
					bookingType: PRODUCT_TYPES.HOLIDAY,
					query: tripDestinationWatcher
				});

				const data = response?.data?.items;
				if (!Array.isArray(data)) {
					return;
				}

				const hasMatchingLocation = data.some((item) => {
					const locationUrlDescription = item?.locationUrlDescription || '';
					const gatewayDescriptions = (item?.locationMetadata?.gateway || [])
						.map((gateway) => gateway?.description?.toLowerCase())
						.filter(Boolean);

					return (
						locationUrlDescription.includes(tripDestinationWatcher) &&
						gatewayDescriptions.includes(tripOriginWatcher)
					);
				});

				const isDurationWithinRange = isDurationValid(tripDateWatcher);

				setShowHolidayBtn(hasMatchingLocation && isDurationWithinRange);
			} catch (error) {
				console.error('Error fetching CJS search response:', error);
			}
		};

		fetchHolidayUrl();
	}, [
		tripOriginWatcher,
		tripDestinationWatcher,
		tripTypeWatcher,
		tripDateWatcher,
		bookingTypeState
	]);

	const isDurationValid = (dates: string[]): boolean => {
		if (!Array.isArray(dates) || dates.length !== 2) return false;

		const [start, end] = dates.map((date) => DateTime.fromISO(date));
		if (!start.isValid || !end.isValid) return false;

		const diffDays = Math.abs(end.diff(start, 'days').days);
		return diffDays >= 1 && diffDays <= 28;
	};

	const onHolidaySearch = async () => {
		const isValid = await formMethods.trigger();
		if (!isValid) {
			console.error('Form validation failed');
			return;
		}

		setHolidaySubmitting(true);

		const { destination, departing } = formMethods.getValues();
		const [firstDepartingDate, secondDepartingDate] = departing;
		const departureDate = DateTime.fromISO(firstDepartingDate).toFormat(
			DATE_FORMATS.URL_DATE
		);
		const durationInDays = calculateDurationInDays(
			firstDepartingDate,
			secondDepartingDate
		);

		const destinationDashCase =
			destination?.cityName.toLowerCase().replace(/\s+/g, '-') || '';

		bookingType.brand = CJS_BRAND.VAH;

		setHolidaySubmitting(true);

		const CJSQuery: CJSSearchRequest = {
			brand: bookingType.brand,
			submissionType: PRODUCT_TYPES.HOLIDAY,
			location: destinationDashCase,
			departureDate: departureDate,
			duration: durationInDays,
			gateway: tripOriginCodeWatcher,
			partyCompositions: Rooms.format(tripRoomsWatcher)
		};

		getCJSSearchUrl(CJSQuery)
			.then((link) => {
				const baseUrl = link.data.url;
				const refs = refsToUrlParams(criteria?.refs);
				const refUrl = 'CTA=AbTest_SP_Holidays';
				const hasParams = baseUrl.includes('?');

				const cleanedRefs = refs?.replace(/&+$/, '');
				let url = `${baseUrl}${hasParams ? '&' : '?'}${cleanedRefs}`;

				if (cleanedRefs) {
					url += `&${refUrl}`;
				} else {
					url += `${refUrl}`;
				}

				window.open(url, '_self');
			})
			.catch((error) => {
				setSubmitting(false);
				setError('root.serverError', {
					type: 'custom',
					message:
						"Oops! We're having some technical issues, please pop back later."
				});
			});
	};

	const onOpenRoomsModal = () => {
		const { passengers } = formMethods.getValues();
		const { adults, teens, children, infants } = passengers;

		const updatedRooms = [
			{
				adults,
				childAges: Array(teens + children).fill(10),
				infants
			}
		];

		setDefaultRoomValues(updatedRooms);

		formMethods.setValue('passengerBreakdown', updatedRooms);

		setIsModalOpen(true);
	};

	const calculateDurationInDays = (
		startDate: string,
		endDate: string
	): number => {
		const start = new Date(startDate);
		const end = new Date(endDate);
		const durationInMs = end.getTime() - start.getTime();
		return Math.ceil(durationInMs / (1000 * 3600 * 24));
	};

	useEffect(() => {
		if (tripTypeWatcher === TRIP_TYPES.MULTICITY.value && !multicityEnabled()) {
			window.open(
				'https://www.virginatlantic.com/flight-search/book-a-flight?tripType=MULTICITY',
				'_self'
			);
		} else {
			setSelectedTripType(tripTypeWatcher);
		}
	}, [tripTypeWatcher]);

	useEffect(() => {
		setValue(
			'payment_type',
			bookingTypeState.type === PRODUCT_TYPES.REWARD_FLIGHT
				? PAYMENT_TYPES.POINTS.value
				: PAYMENT_TYPES.MONEY.value
		);
		setValue('submissionType', bookingTypeState.type, { shouldValidate: true });
	}, [bookingTypeState]);

	const onSubmit = async (data: any) => {
		const isValid = multicityEnabled() ? await formMethods.trigger() : true;

		if (!isValid) {
			console.error('Form validation failed');
			return;
		}

		setSubmitting(true);

		let origins: string[] = [];
		let destinations: string[] = [];
		const departing =
			multicityEnabled() && isMultiCity
				? Object.keys(data)
						.filter((key) => key.startsWith('departing_'))
						.map((key) =>
							DateTime.fromISO(data[key][0]).toFormat(DATE_FORMATS.URL_DATE)
						)
						.filter(Boolean)
				: data.departing.map((value: string) =>
						DateTime.fromISO(value).toFormat(DATE_FORMATS.URL_DATE)
					);

		switch (data.trip_type) {
			case TRIP_TYPES.ONE_WAY.value:
				origins = [data.origin.airportCode];
				destinations = [data.destination.airportCode];
				break;
			case TRIP_TYPES.ROUND_TRIP.value:
				origins = [data.origin.airportCode, data.destination.airportCode];
				destinations = [data.destination.airportCode, data.origin.airportCode];
				break;
			case TRIP_TYPES.MULTICITY.value:
				origins = Object.keys(data)
					.filter((key) => key.startsWith('origin_'))
					.map((key) => data[key]?.airportCode)
					.filter(Boolean);

				destinations = Object.keys(data)
					.filter((key) => key.startsWith('destination_'))
					.map((key) => data[key]?.airportCode)
					.filter(Boolean);
				break;
		}

		const query: FlightSearchRequest = {
			searchType: bookingTypeState.type,
			origin: origins,
			destination: destinations,
			departing: departing,
			passengers: Passengers.format([data.passengers])[0] as string,
			...(data.cabin && { cabin: data.cabin }),
			...(data.discount_code && { discountCode: data.discount_code }),
			...(data.payment_type && {
				options: [
					bookingTypeState.type === PRODUCT_TYPES.REWARD_FLIGHT
						? PAYMENT_TYPES.POINTS.value
						: PAYMENT_TYPES.MONEY.value
				]
			})
		};

		getFlightSearchUrl(query)
			.then((link) => {
				const baseUrl = link.data.url;
				const refs = refsToUrlParams(criteria?.refs)?.replace(/^&/, '') || '';
				const refValue = 'AbTest_SP_Flights';
				const hasParams = baseUrl.includes('?');

				let url = `${baseUrl}${hasParams ? '&' : '?'}${refs}`;

				if (!url.includes('CTA=') && refValue) {
					url += `${refs ? '&' : ''}CTA=${refValue}`;
				}

				window.open(url, '_self');
			})
			.catch((error) => {
				setSubmitting(false);
				setError('root.serverError', {
					type: 'custom',
					message:
						"Oops! We're having some technical issues, please pop back later."
				});
			});
	};

	const isMultiCity = useMemo(
		() => selectedTripType === TRIP_TYPES.MULTICITY.value && multicityEnabled(),
		[selectedTripType]
	);

	return (
		<form
			id={bookingTypeState?.type?.toString()}
			onSubmit={handleSubmit(onSubmit)}>
			<FormProvider {...formMethods}>
				{errors?.root?.serverError?.type === 'custom' && (
					<ServerError>
						<AlertComponent
							emphasis={AlertEmphasis.Low}
							hasIcon
							status={AlertStatus.Error}
							title={errors?.root?.serverError?.message}>
							{''}
						</AlertComponent>
					</ServerError>
				)}

				<SeasonalMessageComponent />

				<FormGrid>
					<FormOptions>
						<Div css={[column, option, cabin]} data-cy="cabin-field">
							<Controller
								name={'cabin'}
								control={control}
								render={({ field }) => (
									<SwitchComponent
										label={'Search for Reward Flights'}
										ariaLabelledBy={'Search for Reward Flights'}
										size={SwitchSize.XSmall}
										labelPosition={SwitchLabelPosition.Right}
										isChecked={
											bookingTypeState.type === PRODUCT_TYPES.REWARD_FLIGHT
										}
										onChange={(isChecked) =>
											setBookingTypeState(() =>
												isChecked
													? bookingTypes.find(
															(t) => t.type === PRODUCT_TYPES.REWARD_FLIGHT
														)
													: bookingTypes.find(
															(t) => t.type === PRODUCT_TYPES.FLIGHT_ONLY
														)
											)
										}
									/>
								)}
							/>
						</Div>

						<input
							readOnly
							type="hidden"
							data-cy="payment-type-field"
							name="payment_type"
							defaultValue={
								bookingTypeState.type === PRODUCT_TYPES.FLIGHT_ONLY
									? PAYMENT_TYPES.MONEY.value
									: PAYMENT_TYPES.POINTS.value
							}
							{...register('payment_type')}
						/>
						{bookingTypeState.type === PRODUCT_TYPES.REWARD_FLIGHT && (
							<Div css={[column, option, rewardText]}>
								<TypographyComponent
									element="p"
									variant={TypographyVariant.Body}
									size={TypographySize.Xsm}
									weight={TypographyWeight.Regular}>
									You'll need to log in to book flights with your Virgin Points
								</TypographyComponent>
							</Div>
						)}

						{bookingTypeState.type === PRODUCT_TYPES.FLIGHT_ONLY && (
							<Div
								css={[
									column,
									option,
									discount,
									isMultiCity && multiCityDiscount
								]}>
								<Controller
									render={({ field: { ref, ...rest }, fieldState }) => (
										<DiscountCodeComponent
											label={'Got a discount code?'}
											name="discount_code"
											{...rest}
											{...fieldState}
										/>
									)}
									name={'discount_code'}
									defaultValue={
										(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.discountCode
									}
									control={control}
								/>
							</Div>
						)}
					</FormOptions>
					{!isMultiCity && (
						<>
							<Div css={[column, mainColumn, flyingColumn]}>
								<Controller
									render={({ field: { ref, ...rest }, fieldState }) => (
										<TypeaheadComponent
											label={'Flying from'}
											placeholder={''}
											popoverTitle={'Flying from'}
											historyTitle={TYPEAHEAD_MESSAGES.historyTitle}
											noResultsLabel={TYPEAHEAD_MESSAGES.noResultsMatch}
											iconChild={<IconTravelTakeOffComponent />}
											id={'flights_from'}
											feedback={errors?.origin?.message?.toString()}
											hasError={!!errors?.origin}
											switchFocusTo={() => handleInputFocus('flights_to')}
											{...rest}
											{...fieldState}
										/>
									)}
									name={'origin'}
									defaultValue={
										(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.origin?.[0] || ''
									}
									control={control}
									rules={{
										required: TYPEAHEAD_MESSAGES.requiredOrigin
									}}
								/>
							</Div>

							<Div css={[column, mainColumn, flyingColumn]}>
								<Controller
									render={({ field: { ref, ...rest }, fieldState }) => (
										<TypeaheadComponent
											label={'Flying to'}
											placeholder={TYPEAHEAD_MESSAGES.locationPlaceholder}
											popoverTitle={'Flying to'}
											historyTitle={TYPEAHEAD_MESSAGES.historyTitle}
											noResultsLabel={TYPEAHEAD_MESSAGES.noResultsMatch}
											iconChild={<IconTravelGlobeMarkerComponent />}
											id={'flights_to'}
											feedback={errors?.destination?.message?.toString()}
											hasError={!!errors?.destination}
											switchFocusTo={() =>
												handleInputFocus('flights_departing')
											}
											{...rest}
											{...fieldState}
										/>
									)}
									name={'destination'}
									defaultValue={
										(criteria as FLIGHT_ONLY | REWARD_FLIGHT)
											?.destination?.[0] || ''
									}
									control={control}
									rules={{
										required: TYPEAHEAD_MESSAGES.requiredDestination
									}}
								/>
							</Div>
						</>
					)}

					<Div
						css={[column, readOnly, tripType, isMultiCity && multiCityInput]}
						data-cy="trip-type-field">
						<Controller
							name={'trip_type'}
							control={control}
							render={({ field: { ref, ...rest }, fieldState }) => (
								<DynamicListSelectorComponent
									label={'Journey'}
									id="trip_type"
									ready={true}
									options={Object.values(TRIP_TYPES).map((type) => {
										return {
											label: type.name,
											value: type.value,
											selectedLabel: type.displayName,
											selected: type.value === defaults?.tripType
										};
									})}
									{...rest}
								/>
							)}
						/>
					</Div>

					{!isMultiCity && (
						<Div
							ref={ref}
							css={[column, mainColumn, date(inputWidths), readOnly]}>
							<Controller
								render={({ field: { ref, ...rest }, fieldState }) => (
									<DatePickerFieldComponent
										label={'When'}
										id={'flights_departing'}
										feedback={errors?.departing?.message?.toString()}
										hasError={Boolean(errors?.departing)}
										bookingType={PRODUCT_TYPES.FLIGHT_ONLY}
										selectionType={
											tripTypeWatcher === TRIP_TYPES.ONE_WAY.value
												? 'single'
												: 'range'
										}
										{...rest}
										{...fieldState}
									/>
								)}
								name={'departing'}
								defaultValue={
									(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.departing || []
								}
								control={control}
								rules={{
									validate: {
										requiredDate: (value) => {
											if (value.length < 1)
												return DATEPICKER_ERROR_MESSAGE.noSelection;
											if (
												tripTypeWatcher === TRIP_TYPES.ONE_WAY.value &&
												value.length !== 1
											)
												return DATEPICKER_ERROR_MESSAGE.oneWay;
											if (
												tripTypeWatcher === TRIP_TYPES.ROUND_TRIP.value &&
												value.length !== 2
											)
												return DATEPICKER_ERROR_MESSAGE.range;
										}
									}
								}}
							/>
						</Div>
					)}

					<Div
						css={[
							column,
							mainColumn,
							passengers(inputWidths),
							readOnly,
							isMultiCity && multiCityInput
						]}>
						<Controller
							render={({ field: { ref, ...rest }, fieldState }) => (
								<PassengerFieldComponent
									label={'Who'}
									placeholder={'Who?'}
									id={'flights_who'}
									feedback={errors?.passengers?.message?.toString()}
									hasError={Boolean(errors?.passengers)}
									{...rest}
									{...fieldState}
								/>
							)}
							name={'passengers'}
							defaultValue={
								(criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.passengers
									? (criteria as FLIGHT_ONLY | REWARD_FLIGHT)?.passengers
									: null
							}
							control={control}
							rules={{
								required: 'This field is required',
								validate: (value) => {
									const messages = [];
									if (value.infants > value.adults + value.teens) {
										messages.push(
											'There needs to be at least one adult or young adult per lap infant.'
										);
									}
									if (
										value.adults +
											value.teens +
											value.children +
											value.infants >
										9
									) {
										messages.push(
											'We love a group! For bookings of more than 9 passengers please call our specialists on 0344 472 1422.'
										);
									}
									return messages.length <= 0 ? true : messages.join('\n');
								}
							}}
						/>
					</Div>
					{multicityEnabled() && isMultiCity && (
						<MultiCityFormComponent control={control} criteria={criteria} />
					)}
					<Div
						css={[
							column,
							mainColumn,
							submit(buttonWidths),
							isMultiCity && multiCityButton,
							showHolidayBtn && multiCtaButton,
							isModalOpen && modalContainer
						]}>
						<ButtonComponent
							data-cy="submit-button"
							type={ButtonType.Submit}
							size={ButtonSize.Medium}
							text={'Search flights'}
							isLoading={submitting}
							loadingText="Search"
						/>
						{showHolidayBtn && (
							<ButtonComponent
								data-cy="submit-button"
								data-test-attr="target-holiday-search-button"
								type={ButtonType.Button}
								size={ButtonSize.Medium}
								priority={ButtonPriority.Primary}
								text={'Search flight + hotel'}
								isLoading={holidaySubmitting}
								loadingText="Search"
								onClick={onOpenRoomsModal}
							/>
						)}
						<ModalComponent
							css={modalContainer}
							isShown={isModalOpen}
							close={() => setIsModalOpen(false)}
							ariaLabelledBy="modal-title"
							headerChildren={
								<Div css={roomsModalHeader}>
									<IconFeedbackInfoComponent />
									<TypographyComponent
										element="h2"
										variant={TypographyVariant.Heading}
										size={TypographySize.Sml}
										weight={TypographyWeight.Regular}>
										Tell us who's travelling
									</TypographyComponent>
								</Div>
							}
							primaryActionButtonProps={{
								onClick: () => {
									onHolidaySearch();
								},
								text: 'Search flight + hotel'
							}}>
							<Div css={roomsModal}>
								<Div css={roomsModalText}>
									<TypographyComponent
										element="p"
										variant={TypographyVariant.Body}
										size={TypographySize.Sml}
										weight={TypographyWeight.Regular}>
										Let us know how many rooms you'd like so we can show you the
										best deals. Prices shown are based on number of rooms and
										people sharing.
									</TypographyComponent>
								</Div>
								<Controller
									render={({ field: { ref, ...rest }, fieldState }) => (
										<RoomCompositionComponent
											name={'holiday-rooms'}
											value={defaultRoomValues}
											{...rest}
											{...fieldState}
										/>
									)}
									name={'passengerBreakdown'}
									defaultValue={
										(criteria as FLIGHT_HOTEL | HOLIDAY | HOTEL)?.rooms ??
										defaultRoomValues
									}
									control={control}
									rules={{
										required: 'This field is required',
										validate: (value: RoomComposition[]) => {
											const messages = [];
											const adults = value.reduce(
												(total, composition) => (total += composition.adults),
												0
											);
											const infants = value.reduce(
												(total, composition) => (total += composition.infants),
												0
											);
											const noAge = value.reduce(
												(total, composition) =>
													(total += composition.childAges.filter(
														(age) => !age
													).length),
												0
											);
											const total = value.reduce(
												(total, composition) =>
													(total +=
														composition.adults +
														composition.childAges.length +
														composition.infants),
												0
											);
											const rooms = value.length;
											if (noAge > 0) {
												messages.push('Please enter all child ages.');
											}
											if (infants > adults) {
												messages.push(
													'There must be at least one adult per child under two years of age.'
												);
											}
											if (total > 9) {
												messages.push(
													'We love a group! For bookings of more than 9 passengers please call our specialists on 0344 472 1422.'
												);
											}
											if (rooms > 4) {
												messages.push(
													'Whoops! Looks like you have more than 4 rooms. Please remove any extra rooms.'
												);
											}
											return messages.length <= 0 ? true : messages.join('\n');
										}
									}}
								/>
							</Div>
						</ModalComponent>
					</Div>
				</FormGrid>
				<input
					readOnly
					type="hidden"
					defaultValue={bookingTypeState.type}
					{...register('submissionType')}
				/>
			</FormProvider>
		</form>
	);
};

export default FlightOnlyComponent;
